import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowRight, Bot, CheckCircle2, MapPin, Clock, ShieldCheck, Star, Cog } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ServiceIcon, SectionLabel, priceLabel } from "@/components/public/common";

export default function Home() {
  const { data: content } = useQuery({ queryKey: ["content"], queryFn: () => api.get("/content").then(r => r.data.content) });
  const { data: services } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then(r => r.data.services) });
  const { data: pricing } = useQuery({ queryKey: ["pricing"], queryFn: () => api.get("/pricing").then(r => r.data.pricing) });
  const { data: testimonials } = useQuery({ queryKey: ["testimonials"], queryFn: () => api.get("/testimonials").then(r => r.data.items) });
  const { data: posts } = useQuery({ queryKey: ["blog"], queryFn: () => api.get("/blog").then(r => r.data.posts) });

  const hero = content?.home_hero || {};
  const contact = content?.contact_info || {};
  const featured = (pricing || []).filter(p => p.featured).slice(0, 3);
  const faqs = content?.faqs || [];

  return (
    <div>
      {/* HERO */}
      <section className="relative tdw-hero-gradient text-white overflow-hidden">
        <div className="absolute inset-0 tdw-circuit opacity-60" />
        <Cog className="tdw-gear absolute -right-16 -top-16 h-72 w-72 text-white/5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="md:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-mono text-tech mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-tech animate-pulse" /> Intelligent Business Assistance, 24/7
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              {hero.headline || "Web Development, PC Troubleshooting & Automation Solutions"}
            </h1>
            <p className="mt-6 text-slate-300 text-base md:text-lg max-w-2xl leading-relaxed">
              {hero.subheadline}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/chat"><Button size="lg" className="bg-electric hover:bg-tech gap-2" data-testid="hero-chat-btn"><Bot className="h-5 w-5" /> Chat with Nexus</Button></Link>
              <Link to="/request-service"><Button size="lg" variant="outline" className="border-white/30 bg-white/5 text-white hover:bg-white hover:text-navy gap-2" data-testid="hero-request-btn">Request Service <ArrowRight className="h-4 w-4" /></Button></Link>
              <Link to="/book"><Button size="lg" variant="ghost" className="text-white hover:bg-white/10" data-testid="hero-book-btn">Schedule Consultation</Button></Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
              <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-tech" /> {contact.area || "Southern Illinois"}</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-tech" /> {contact.hours || "Nexus AI 24/7"}</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-tech" /> Local. Affordable. Honest.</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="md:col-span-5">
            <div className="rounded-2xl tdw-glass p-1 shadow-2xl">
              <div className="rounded-xl bg-navy/80 border border-white/10 p-6">
                <div className="flex items-center gap-2 mb-4"><Bot className="h-5 w-5 text-tech" /><span className="font-heading font-semibold">Nexus is ready to help</span></div>
                <div className="space-y-2">
                  {["PC running slow?", "Need a new website?", "Automate busywork?"].map((q, i) => (
                    <Link to="/chat" key={i} className="block rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-3 text-sm transition-colors">
                      {q} <ArrowRight className="inline h-3.5 w-3.5 ml-1 text-tech" />
                    </Link>
                  ))}
                </div>
                <p className="mt-4 text-[11px] text-slate-400">You'll be chatting with an AI assistant.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <SectionLabel>What we do</SectionLabel>
        <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-navy max-w-2xl">Practical digital solutions for small business</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {(services || []).map((s) => (
            <Link to={`/services/${s.slug}`} key={s.id} data-testid={`service-card-${s.slug}`}
              className="group rounded-xl border border-slate-200 p-6 hover:border-electric hover:shadow-lg transition-all">
              <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-electric to-tech text-white grid place-items-center mb-4 group-hover:scale-105 transition-transform">
                <ServiceIcon name={s.icon} />
              </div>
              <h3 className="font-heading font-semibold text-lg text-navy">{s.name}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.shortDescription}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-electric">Learn more <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" /></span>
            </Link>
          ))}
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <SectionLabel>Our process</SectionLabel>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-navy">Simple, transparent, honest</h2>
          <div className="mt-10 grid md:grid-cols-4 gap-5">
            {[["01", "Tell us what you need", "Chat with Nexus or submit a request — no pressure, no jargon."],
              ["02", "We review & assess", "We diagnose the issue or scope your project and prepare an honest estimate."],
              ["03", "You approve", "Clear pricing up front. Nothing happens without your OK."],
              ["04", "We deliver", "Fast, reliable work with progress you can track anytime."]].map(([n, t, d]) => (
              <div key={n} className="rounded-xl bg-white border border-slate-200 p-6">
                <span className="font-mono text-2xl font-bold text-electric/30">{n}</span>
                <h3 className="mt-2 font-heading font-semibold text-navy">{t}</h3>
                <p className="mt-2 text-sm text-slate-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING PREVIEW */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <SectionLabel>Popular packages</SectionLabel>
              <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-navy">Affordable, upfront pricing</h2>
            </div>
            <Link to="/pricing"><Button variant="outline" data-testid="view-all-pricing">View all pricing <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {featured.map((p) => (
              <div key={p.id} className="rounded-xl border-2 border-electric/20 p-6 relative overflow-hidden">
                <span className="absolute top-4 right-4 text-[10px] font-mono uppercase tracking-wider text-electric bg-electric/10 px-2 py-0.5 rounded-full">{p.category}</span>
                <h3 className="font-heading font-semibold text-lg text-navy">{p.packageName}</h3>
                <p className="mt-2 text-3xl font-heading font-bold text-navy">{priceLabel(p)}</p>
                <p className="mt-1 text-sm text-slate-600">{p.description}</p>
                <ul className="mt-4 space-y-2">
                  {(p.features || []).slice(0, 5).map((f, i) => (
                    <li key={i} className="flex gap-2 text-sm text-slate-700"><CheckCircle2 className="h-4 w-4 text-electric shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
                <Link to="/request-service"><Button className="w-full mt-6 bg-electric hover:bg-tech">Get Started</Button></Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {testimonials?.length > 0 && (
        <section className="tdw-navy-bg text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
            <SectionLabel>What locals say</SectionLabel>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Trusted by Southern Illinois</h2>
            <div className="mt-10 grid md:grid-cols-3 gap-5">
              {testimonials.map((t, i) => (
                <div key={i} className="rounded-xl bg-white/5 border border-white/10 p-6">
                  <div className="flex gap-0.5 mb-3">{Array.from({ length: t.rating || 5 }).map((_, j) => <Star key={j} className="h-4 w-4 fill-tech text-tech" />)}</div>
                  <p className="text-slate-200 text-sm leading-relaxed">"{t.quote}"</p>
                  <p className="mt-4 font-heading font-semibold text-sm">{t.name}</p>
                  <p className="text-xs text-slate-400">{t.location}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BLOG */}
      {posts?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div><SectionLabel>From the blog</SectionLabel><h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-navy">Tips & insights</h2></div>
            <Link to="/blog"><Button variant="outline">All articles <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {posts.slice(0, 3).map((p) => (
              <Link to={`/blog/${p.slug}`} key={p.id} className="group rounded-xl border border-slate-200 p-6 hover:shadow-lg transition-all">
                <span className="text-[10px] font-mono uppercase tracking-wider text-electric">{p.category}</span>
                <h3 className="mt-2 font-heading font-semibold text-navy group-hover:text-electric transition-colors">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 line-clamp-2">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ + CTA */}
      <section className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20">
          <SectionLabel>Questions</SectionLabel>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-navy">Frequently asked</h2>
          <div className="mt-8 space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="group rounded-xl bg-white border border-slate-200 p-5">
                <summary className="cursor-pointer font-heading font-medium text-navy list-none flex justify-between items-center">{f.q}<span className="text-electric group-open:rotate-45 transition-transform text-xl">+</span></summary>
                <p className="mt-3 text-sm text-slate-600">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-12 rounded-2xl tdw-hero-gradient text-white p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 tdw-circuit opacity-40" />
            <div className="relative">
              <h3 className="font-heading text-2xl md:text-3xl font-bold">Ready to get started?</h3>
              <p className="mt-2 text-slate-300">Chat with Nexus now — it's free and available 24/7.</p>
              <div className="mt-6 flex justify-center gap-3 flex-wrap">
                <Link to="/chat"><Button size="lg" className="bg-electric hover:bg-tech gap-2"><Bot className="h-5 w-5" /> Chat with Nexus</Button></Link>
                <Link to="/contact"><Button size="lg" variant="outline" className="border-white/30 bg-white/5 text-white hover:bg-white hover:text-navy">Contact Us</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
